import { useId, useState } from "react";

import {
  errorMessage,
  useCancelDeletion,
  useDeletion,
  useRequestDeletion,
} from "../../api/queries";
import type { Dataset, Me } from "../../api/types";
import { useStepUp } from "../../auth/useStepUp";
import { formatDateTime } from "../../lib/format";
import { Dialog } from "../ui/Dialog";
import { Field } from "../ui/Field";
import { ServerLines } from "../ui/ServerLines";
import { ErrorBlock, LoadingBlock } from "../ui/StateBlock";

interface RequestDeletionDialogProps {
  dataset: Dataset;
  /** Whether this person may skip the grace window; only an admin may. */
  isAdmin: boolean;
  onClose: () => void;
}

/**
 * Ask for a client's bytes to be destroyed.
 *
 * Scheduled a day out and cancellable in that window, because "delete
 * Decathlon" typed into the wrong row at 19:00 on a Friday is a thing that
 * happens. It asks for your code, and the reason goes on the receipt.
 *
 * Args:
 *     dataset: The dataset whose bytes would go.
 *     isAdmin: Whether the "now, the client asked for it" option is offered.
 *     onClose: Close the dialog.
 *
 * Returns:
 *     The dialog, or the code prompt while one is being asked for.
 */
function RequestDeletionDialog({
  dataset,
  isAdmin,
  onClose,
}: RequestDeletionDialogProps): JSX.Element {
  const reasonId = useId();
  const [reason, setReason] = useState("");
  const [immediate, setImmediate] = useState(false);
  const [lines, setLines] = useState<string[] | null>(null);
  const request = useRequestDeletion(dataset.id);
  const stepUp = useStepUp();

  const submit = (): void => {
    request.mutate(
      { reason: reason.trim(), immediate },
      {
        onSuccess: (scheduled) => setLines(scheduled.lines),
        onError: (error) => {
          stepUp.capture(error, () => submit());
        },
      },
    );
  };

  if (stepUp.dialog) {
    return stepUp.dialog;
  }

  if (lines) {
    return (
      <Dialog
        title="Scheduled"
        onClose={onClose}
        footer={
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Close
          </button>
        }
      >
        <ServerLines lines={lines} />
      </Dialog>
    );
  }

  return (
    <Dialog
      title={`Erase ${dataset.account.name}'s ${dataset.slug}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={request.isPending || reason.trim().length === 0}
            onClick={submit}
          >
            {request.isPending ? "Scheduling…" : "Schedule the erasure"}
          </button>
        </>
      }
    >
      <p className="dialog__text">
        The files go, from both regions, and the console checks afterwards that
        every one of them is gone before it writes the receipt. The record
        stays: this dataset, its trace, who read it and why, the checksums, the
        results recorded against it, and the receipt itself.
      </p>

      <Field
        label="Why?"
        htmlFor={reasonId}
        hint="It goes on the receipt the client is sent."
        error={
          request.isError && !stepUp.dialog
            ? errorMessage(request.error)
            : undefined
        }
      >
        <input
          id={reasonId}
          className="input"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Acme asked us to erase it — e-mail from C. Meyer, 12 August 2026"
        />
      </Field>

      {isAdmin ? (
        <label className="check">
          <input
            type="checkbox"
            checked={immediate}
            onChange={(event) => setImmediate(event.target.checked)}
          />
          <span>
            Now, because the client asked for it now — this skips the day you
            would otherwise have to change your mind, and the receipt says which
            of the two it was.
          </span>
        </label>
      ) : null}

      <p className="dialog__warning">
        The console will ask for your code. Until the erasure runs, the dataset
        reads exactly as it does now, and links already issued keep working
        until they expire.
      </p>
    </Dialog>
  );
}

interface DeletionSectionProps {
  dataset: Dataset;
  me: Me;
}

/**
 * Whether anybody has asked for this client's bytes to go, and what happened.
 *
 * Args:
 *     dataset: The dataset.
 *     me: The signed-in account, for the admin-only immediate option.
 *
 * Returns:
 *     The deletion section.
 */
export function DeletionSection({
  dataset,
  me,
}: DeletionSectionProps): JSX.Element {
  const [asking, setAsking] = useState(false);
  const scheduled = dataset.deletion;
  const cancel = useCancelDeletion(dataset.id);
  // Only asked for once there is one: a dataset nobody asked to erase answers
  // `404`, which is a state and not a failure — so the query is not started.
  const deletion = useDeletion(dataset.id, scheduled !== null);

  if (dataset.deleted_at) {
    return (
      <section className="section">
        <div className="section__head">
          <h2 className="section__title">This file was erased</h2>
        </div>
        {deletion.isPending ? (
          <LoadingBlock label="Reading the receipt…" />
        ) : deletion.isError ? (
          <ErrorBlock
            error={deletion.error}
            onRetry={() => void deletion.refetch()}
          />
        ) : deletion.data.receipt ? (
          <>
            <ServerLines lines={deletion.data.receipt.lines} />
            <p className="form__note">
              <a href={`/datasets/${dataset.id}/receipt`}>
                Open the receipt to print or forward it
              </a>
            </p>
          </>
        ) : (
          <p className="state-block">
            The erasure has not finished, so there is no receipt yet. The
            console writes one only after it has checked, in both regions, that
            every object is gone.
          </p>
        )}
      </section>
    );
  }

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Erasing this client's file</h2>
        {scheduled ? null : (
          <button
            type="button"
            className="btn btn--sm btn--danger"
            onClick={() => setAsking(true)}
          >
            Ask for it to be erased
          </button>
        )}
      </div>

      {scheduled ? (
        <>
          <ServerLines lines={scheduled.lines} />
          <p className="form__note">
            Asked for by {scheduled.requested_by},{" "}
            {formatDateTime(scheduled.requested_at)}.
          </p>
          {cancel.isError ? (
            <p className="form__error" role="alert">
              {errorMessage(cancel.error)}
            </p>
          ) : null}
          <button
            type="button"
            className="btn btn--sm"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            {cancel.isPending ? "Cancelling…" : "Cancel it — keep the file"}
          </button>
        </>
      ) : (
        <p className="section__lead">
          Nobody has asked for this to be erased. When somebody does, it is
          scheduled a day out, cancellable until then, and the console writes a
          receipt naming every object it destroyed — only after checking each
          one is gone in both regions.
        </p>
      )}

      {asking ? (
        <RequestDeletionDialog
          dataset={dataset}
          isAdmin={me.is_admin}
          onClose={() => setAsking(false)}
        />
      ) : null}
    </section>
  );
}
